import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Popconfirm, Radio, Select, Space, Switch, Table, Tag, message } from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import api from '../../../../services/api';

const { TextArea } = Input;

const EMPTY_RULE = {
  name: '',
  content: '',
  description: '',
  titleKeywords: [],
  bulletKeywords: [],
  genericKeywords: [],
  skuPatterns: [],
  nicheType: 'main',
  parentMainNicheId: null,
  parentSubNicheId: null,
  priority: null,
  isActive: true
};

const RuleSettings = ({ type }) => {
  const isQuote = type === 'quote';
  const endpoint = isQuote ? '/quotes' : '/niches';
  const title = isQuote ? 'Quote' : 'Niche';

  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rescanLoading, setRescanLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [nicheTypeFilter, setNicheTypeFilter] = useState('all');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [unmappedModalOpen, setUnmappedModalOpen] = useState(false);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [unmappedDownloading, setUnmappedDownloading] = useState(false);
  const [unmappedData, setUnmappedData] = useState({ total: 0, rows: [] });
  const [parentOptions, setParentOptions] = useState({ main: [], sub: [] });
  const [parentOptionsLoading, setParentOptionsLoading] = useState(false);
  const nicheType = Form.useWatch('nicheType', form);

  const fetchRules = useCallback(async (page = pagination.current, pageSize = pagination.pageSize, overrides = {}) => {
    setLoading(true);
    try {
      const typeFilter = overrides.nicheTypeFilter !== undefined ? overrides.nicheTypeFilter : nicheTypeFilter;
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('pageSize', pageSize);
      if (search) params.append('search', search);
      if (!isQuote && typeFilter !== 'all') params.append('type', typeFilter);

      const response = await api.get(`${endpoint}?${params.toString()}`);
      setRows(response.data.rows || []);
      setPagination({
        current: response.data.pagination?.page || page,
        pageSize: response.data.pagination?.pageSize || pageSize,
        total: response.data.pagination?.total || response.data.count || 0
      });
    } catch (error) {
      message.error(error.response?.data?.message || `Failed to load ${title} rules`);
    } finally {
      setLoading(false);
    }
  }, [endpoint, isQuote, nicheTypeFilter, pagination.current, pagination.pageSize, search, title]);

  useEffect(() => {
    fetchRules(1, pagination.pageSize);
  }, []);

  const fetchParentNicheOptions = useCallback(async () => {
    if (isQuote) return parentOptions;
    setParentOptionsLoading(true);
    try {
      const [mainResponse, subResponse] = await Promise.all([
        api.get('/niches?page=1&pageSize=500&type=main&isActive=true&sortBy=name&sortOrder=ASC'),
        api.get('/niches?page=1&pageSize=500&type=sub&isActive=true&sortBy=name&sortOrder=ASC')
      ]);
      const nextOptions = {
        main: mainResponse.data?.rows || [],
        sub: subResponse.data?.rows || []
      };
      setParentOptions(nextOptions);
      return nextOptions;
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load parent niches');
      return parentOptions;
    } finally {
      setParentOptionsLoading(false);
    }
  }, [isQuote, parentOptions]);

  const openCreate = async () => {
    setEditingRecord(null);
    form.setFieldsValue({
      ...EMPTY_RULE,
      nicheType: 'main',
      parentMainNicheId: null,
      parentSubNicheId: null,
      priority: null
    });
    setModalOpen(true);
    fetchParentNicheOptions();
  };

  const openEdit = async (record) => {
    setEditingRecord(record);
    const options = await fetchParentNicheOptions();
    const parentIsSub = Boolean(record.parentNicheId && (options.sub || []).some(item => item.id === record.parentNicheId));
    form.setFieldsValue({
      name: record.name,
      content: record.content || '',
      description: record.description || '',
      titleKeywords: record.titleKeywords || [],
      bulletKeywords: record.bulletKeywords || [],
      genericKeywords: record.genericKeywords || [],
      skuPatterns: record.skuPatterns || [],
      nicheType: record.parentNicheId ? 'sub' : 'main',
      parentMainNicheId: record.parentNicheId && !parentIsSub ? record.parentNicheId : null,
      parentSubNicheId: record.parentNicheId && parentIsSub ? record.parentNicheId : null,
      priority: record.priority ?? null,
      isActive: record.isActive
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = { ...values };
    if (!isQuote) {
      const parentNicheId = values.nicheType === 'sub'
        ? values.parentSubNicheId || values.parentMainNicheId || null
        : null;
      if (values.nicheType === 'sub' && !parentNicheId) {
        message.error('Parent niche is required for sub niche');
        return;
      }
      payload.parentNicheId = parentNicheId;
      payload.priority = values.priority === undefined || values.priority === null || values.priority === ''
        ? null
        : values.priority;
      delete payload.nicheType;
      delete payload.parentMainNicheId;
      delete payload.parentSubNicheId;
    } else {
      delete payload.priority;
    }
    setSaving(true);
    try {
      if (editingRecord) {
        await api.put(`${endpoint}/${editingRecord.id}`, payload);
        message.success(`${title} updated`);
      } else {
        await api.post(endpoint, payload);
        message.success(`${title} created`);
      }
      setModalOpen(false);
      fetchRules(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data?.message || `Failed to save ${title}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await api.delete(`${endpoint}/${record.id}`);
      message.success(`${title} deleted`);
      fetchRules(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data?.message || `Failed to delete ${title}`);
    }
  };

  const handleRescan = async (record = null) => {
    setRescanLoading(true);
    try {
      const url = isQuote && record ? `${endpoint}/${record.id}/rescan` : `${endpoint}/rescan`;
      const response = await api.post(url);
      message.success(response.data?.message || (isQuote ? `${title} rescan queued` : 'Niche mappings recompute queued'));
    } catch (error) {
      message.error(error.response?.data?.message || `Failed to queue ${title} recompute`);
    } finally {
      setRescanLoading(false);
    }
  };

  const handlePreviewRescan = async (record = null) => {
    setRescanLoading(true);
    try {
      const url = isQuote && record ? `${endpoint}/${record.id}/rescan/preview` : `${endpoint}/rescan/preview`;
      const response = await api.post(url);
      const summary = (response.data?.results || [])
        .map(item => `${item.target}: scanned ${item.scanned}, raw matches ${item.rawMatchCount || item.inserted}, final mappings ${item.inserted}`)
        .join('; ');
      Modal.info({
        title: isQuote ? `${title} Rescan Preview` : 'Niche Recompute Preview',
        content: summary || 'No matched SKU found'
      });
    } catch (error) {
      message.error(error.response?.data?.message || `Failed to preview ${title} rescan`);
    } finally {
      setRescanLoading(false);
    }
  };

  const handleOpenUnmappedSkus = async () => {
    setUnmappedModalOpen(true);
    setUnmappedLoading(true);
    try {
      const response = await api.get('/niches/unmapped-skus');
      setUnmappedData({
        total: Number(response.data?.total || 0),
        rows: response.data?.rows || []
      });
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load unmapped niche SKUs');
    } finally {
      setUnmappedLoading(false);
    }
  };

  const handleDownloadUnmappedSkus = async () => {
    setUnmappedDownloading(true);
    try {
      const response = await api.get('/niches/unmapped-skus/export', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);

      link.href = url;
      link.download = filenameMatch?.[1] || 'unmapped_niche_skus.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Unmapped niche SKU CSV downloaded');
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to download unmapped niche SKU CSV');
    } finally {
      setUnmappedDownloading(false);
    }
  };

  const handleBatchStatus = async (isActive) => {
    if (selectedRowKeys.length === 0) return;
    setSaving(true);
    try {
      await api.patch(`${endpoint}/batch-status`, { ids: selectedRowKeys, isActive });
      message.success(`${selectedRowKeys.length} ${title} rules updated`);
      setSelectedRowKeys([]);
      fetchRules(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data?.message || `Failed to update ${title} rules`);
    } finally {
      setSaving(false);
    }
  };

  const tagList = (items = [], color = 'blue') => {
    if (!items || items.length === 0) return <span style={{ color: '#999' }}>-</span>;
    return (
      <Space size={[4, 4]} wrap>
        {items.slice(0, 5).map(item => <Tag key={item} color={color}>{item}</Tag>)}
        {items.length > 5 && <Tag>+{items.length - 5}</Tag>}
      </Space>
    );
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 220,
        render: (value, record) => (
          <div>
            <strong>{value}</strong>
            {record.description && <div style={{ fontSize: 12, color: '#777' }}>{record.description}</div>}
          </div>
        )
      }
    ];

    if (isQuote) {
      baseColumns.push({
        title: 'Content',
        dataIndex: 'content',
        key: 'content',
        ellipsis: true,
        width: 260
      });
    }

    if (!isQuote) {
      baseColumns.push(
        {
          title: 'Type',
          dataIndex: 'type',
          key: 'type',
          width: 110,
          render: (value) => (
            <Tag color={value === 'sub' ? 'blue' : 'gold'}>
              {value === 'sub' ? 'Sub' : 'Main'}
            </Tag>
          )
        },
        {
          title: 'Priority',
          dataIndex: 'priority',
          key: 'priority',
          width: 100,
          align: 'center',
          render: (value) => (
            value === null || value === undefined
              ? <Tag>Lowest</Tag>
              : <Tag color="blue">{value}</Tag>
          )
        },
        {
          title: 'Parent',
          dataIndex: 'parentName',
          key: 'parentName',
          width: 220,
          render: (value, record) => (
            value ? (
              <div>
                <strong>{value}</strong>
                {record.path && <div style={{ fontSize: 12, color: '#777' }}>{record.path}</div>}
              </div>
            ) : <span style={{ color: '#999' }}>-</span>
          )
        }
      );
    }

    return [
      ...baseColumns,
      {
        title: 'Title Keywords',
        dataIndex: 'titleKeywords',
        key: 'titleKeywords',
        render: (items) => tagList(items, 'geekblue')
      },
      {
        title: 'Bullet Keywords',
        dataIndex: 'bulletKeywords',
        key: 'bulletKeywords',
        render: (items) => tagList(items, 'purple')
      },
      ...(!isQuote ? [{
        title: 'Generic Keywords',
        dataIndex: 'genericKeywords',
        key: 'genericKeywords',
        render: (items) => tagList(items, 'cyan')
      }] : []),
      ...(!isQuote ? [{
        title: 'SKU Patterns',
        dataIndex: 'skuPatterns',
        key: 'skuPatterns',
        render: (items) => tagList(items, 'green')
      }] : []),
      {
        title: 'Mapped SKU',
        dataIndex: 'matchCount',
        key: 'matchCount',
        width: 100,
        align: 'center',
        render: (value) => Number(value || 0).toLocaleString()
      },
      {
        title: 'Status',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 100,
        align: 'center',
        render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'Active' : 'Inactive'}</Tag>
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 150,
        align: 'center',
        render: (_, record) => (
          <Space size={4}>
            {isQuote ? (
              <>
                <Button size="small" icon={<SyncOutlined />} onClick={() => handleRescan(record)} />
                <Button size="small" onClick={() => handlePreviewRescan(record)}>Dry</Button>
              </>
            ) : null}
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
            <Popconfirm title={`Delete this ${title}?`} onConfirm={() => handleDelete(record)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];
  }, [isQuote, title]);

  const unmappedColumns = useMemo(() => [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 180,
      fixed: 'left'
    },
    {
      title: 'ASIN',
      dataIndex: 'asin',
      key: 'asin',
      width: 130
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 360,
      ellipsis: true
    },
    {
      title: 'Seller',
      key: 'seller',
      width: 220,
      render: (_, record) => (
        <div>
          <strong>{record.sellerAccount?.name || '-'}</strong>
          {record.sellerAccount?.sellerId && (
            <div style={{ fontSize: 12, color: '#777' }}>{record.sellerAccount.sellerId}</div>
          )}
        </div>
      )
    },
    {
      title: 'Marketplace',
      dataIndex: 'marketplaceId',
      key: 'marketplaceId',
      width: 150
    },
    {
      title: 'Channel',
      dataIndex: 'fulfillmentChannel',
      key: 'fulfillmentChannel',
      width: 100,
      align: 'center'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center'
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: value => value ? new Date(value).toLocaleString() : '-'
    }
  ], []);

  return (
    <div>
      {!isQuote && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Niche mapping is exclusive: each SKU is assigned to the deepest matched niche, then lowest priority at the same depth. Blank priority is lowest. Recompute always evaluates all active niches."
        />
      )}
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Space wrap>
          <Input.Search
            placeholder={`Search ${title}`}
            allowClear
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onSearch={() => fetchRules(1, pagination.pageSize)}
            style={{ width: 260 }}
          />
          {!isQuote && (
            <Select
              value={nicheTypeFilter}
              style={{ width: 130 }}
              options={[
                { value: 'all', label: 'All niches' },
                { value: 'main', label: 'Main' },
                { value: 'sub', label: 'Sub' }
              ]}
              onChange={(value) => {
                setNicheTypeFilter(value);
                fetchRules(1, pagination.pageSize, { nicheTypeFilter: value });
              }}
            />
          )}
          <Button icon={<ReloadOutlined />} onClick={() => fetchRules(pagination.current, pagination.pageSize)}>
            Reload
          </Button>
          <Button icon={<SyncOutlined />} loading={rescanLoading} onClick={() => handleRescan()}>
            {isQuote ? 'Rescan All' : 'Recompute Niches'}
          </Button>
          <Button loading={rescanLoading} onClick={() => handlePreviewRescan()}>
            {isQuote ? 'Dry Run' : 'Preview Recompute'}
          </Button>
          {!isQuote && (
            <Button icon={<UnorderedListOutlined />} onClick={handleOpenUnmappedSkus}>
              Unmapped SKU
            </Button>
          )}
          <Button disabled={selectedRowKeys.length === 0} loading={saving} onClick={() => handleBatchStatus(true)}>
            Activate
          </Button>
          <Button disabled={selectedRowKeys.length === 0} loading={saving} onClick={() => handleBatchStatus(false)}>
            Deactivate
          </Button>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New {title}
        </Button>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true
        }}
        onChange={(nextPagination) => fetchRules(nextPagination.current, nextPagination.pageSize)}
      />

      <Modal
        title={`${editingRecord ? 'Edit' : 'New'} ${title}`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={EMPTY_RULE}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input />
          </Form.Item>
          {isQuote && (
            <Form.Item name="content" label="Quote Content" rules={[{ required: true, message: 'Content is required' }]}>
              <TextArea rows={3} />
            </Form.Item>
          )}
          <Form.Item name="description" label="Description">
            <TextArea rows={2} />
          </Form.Item>
          {!isQuote && (
            <>
              <Form.Item name="nicheType" label="Niche Type">
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  options={[
                    { label: 'Main niche', value: 'main' },
                    { label: 'Sub niche', value: 'sub' }
                  ]}
                  onChange={(event) => {
                    if (event.target.value === 'main') {
                      form.setFieldsValue({ parentMainNicheId: null, parentSubNicheId: null });
                    }
                  }}
                />
              </Form.Item>
              {nicheType === 'sub' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                  <Form.Item label="Parent main niche" name="parentMainNicheId" style={{ marginBottom: 0 }}>
                    <Select
                      allowClear
                      showSearch
                      loading={parentOptionsLoading}
                      placeholder="Search main niche"
                      optionFilterProp="label"
                      options={(parentOptions.main || [])
                        .filter(item => item.id !== editingRecord?.id)
                        .map(item => ({ value: item.id, label: item.name }))}
                      onChange={(value) => form.setFieldsValue({
                        parentMainNicheId: value || null,
                        parentSubNicheId: null
                      })}
                    />
                  </Form.Item>
                  <Form.Item label="Parent sub niche" name="parentSubNicheId" style={{ marginBottom: 0 }}>
                    <Select
                      allowClear
                      showSearch
                      loading={parentOptionsLoading}
                      placeholder="Search sub niche"
                      optionFilterProp="label"
                      options={(parentOptions.sub || [])
                        .filter(item => item.id !== editingRecord?.id)
                        .map(item => ({ value: item.id, label: item.path || item.name }))}
                      onChange={(value) => form.setFieldsValue({
                        parentSubNicheId: value || null,
                        parentMainNicheId: null
                      })}
                    />
                  </Form.Item>
                </div>
              )}
              <Form.Item
                name="priority"
                label="Priority"
                tooltip="Lower number wins when matched niches have the same depth. Blank is lowest priority."
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === null || value === undefined || value === '') return Promise.resolve();
                      return Number.isInteger(Number(value))
                        ? Promise.resolve()
                        : Promise.reject(new Error('Priority must be an integer'));
                    }
                  }
                ]}
              >
                <InputNumber precision={0} style={{ width: '100%' }} placeholder="Blank = lowest priority" />
              </Form.Item>
            </>
          )}
          <Form.Item name="titleKeywords" label="Title Keywords">
            <Select mode="tags" tokenSeparators={[',']} placeholder="Type keyword and press Enter" />
          </Form.Item>
          <Form.Item name="bulletKeywords" label="Bullet Keywords">
            <Select mode="tags" tokenSeparators={[',']} placeholder="Type keyword and press Enter" />
          </Form.Item>
          {!isQuote && (
            <Form.Item name="genericKeywords" label="Generic Keywords">
              <Select mode="tags" tokenSeparators={[',']} placeholder="Type keyword and press Enter" />
            </Form.Item>
          )}
          {!isQuote && (
            <Form.Item name="skuPatterns" label="SKU Patterns">
              <Select mode="tags" tokenSeparators={[',']} placeholder="Type SKU pattern and press Enter" />
            </Form.Item>
          )}
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {!isQuote && (
        <Modal
          title="SKUs Without Niche Mapping"
          open={unmappedModalOpen}
          onCancel={() => setUnmappedModalOpen(false)}
          width={1180}
          destroyOnClose
          footer={[
            <Button key="close" onClick={() => setUnmappedModalOpen(false)}>
              Close
            </Button>,
            <Button
              key="download"
              type="primary"
              icon={<DownloadOutlined />}
              loading={unmappedDownloading}
              onClick={handleDownloadUnmappedSkus}
            >
              Download All CSV
            </Button>
          ]}
        >
          <Alert
            type={unmappedData.total > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
            message={`${unmappedData.total.toLocaleString()} SKUs have no niche mapping`}
            description="The table shows the 100 most recently updated SKUs. The CSV contains the complete list with seller information."
          />
          <Table
            rowKey="id"
            columns={unmappedColumns}
            dataSource={unmappedData.rows}
            loading={unmappedLoading}
            pagination={false}
            size="small"
            scroll={{ x: 1420, y: 520 }}
          />
        </Modal>
      )}
    </div>
  );
};

export default RuleSettings;
